import { Accordion } from '@/components/ui/accordion';
import { useLocation } from 'react-router-dom';
import { useCallback, useMemo } from 'react';
import { AccountNavArchived } from './account-nav-archived';
import { AccountNavGroups } from './account-nav-groups';
import { getAccountGroups, useAccounts, useArchivedAccounts, useBudgetId } from '@liquidbudget/common';
import { useUiStore } from '@/components/ui/use-ui-store';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CreditCard, HandCoins, PiggyBank, Wallet } from 'lucide-react';
import { AccountAddDialog } from '../account-form/account-add-dialog';
import { Button } from '@/components/ui/button';

type Props = {};

/**
 * Creates a safe accordion value from a group name by converting to lowercase
 * and replacing spaces with hyphens.
 */
function createAccordionValue(groupName: string): string {
  return groupName.toLowerCase().replace(/\s+/g, '-');
}

export function AccountNav({}: Props) {
  const location = useLocation();
  const accounts = useAccounts();
  const inactiveAccounts = useArchivedAccounts();
  const budgetId = useBudgetId();

  const { groups } = useMemo(() => getAccountGroups(accounts), [accounts]);
  const budgetState = useUiStore((state) => state.budgets[budgetId]);
  const setAccountNavGroupCollapsed = useUiStore((state) => state.setAccountNavGroupCollapsed);

  // Check if there are no accounts
  const hasNoAccounts = accounts.length === 0;

  // Determine which accordion sections should be open
  const accordionValues = useMemo(() => {
    const values: string[] = [];

    // Add open groups (default to open if no stored preference)
    groups.forEach((group) => {
      const accordionValue = createAccordionValue(group.name);
      const isCollapsed = budgetState?.accountNavGroupsCollapsed?.[group.name] ?? false;

      if (!isCollapsed) {
        values.push(accordionValue);
      }
    });

    // Handle archived accounts - add if not collapsed (default to closed)
    const isArchivedCollapsed = budgetState?.accountNavGroupsCollapsed?.['Archived'] ?? true;
    if (!isArchivedCollapsed) {
      values.push('archived');
    }

    return values;
  }, [groups, budgetState?.accountNavGroupsCollapsed, location.pathname, inactiveAccounts]);

  // Handle accordion state changes
  const handleAccordionChange = useCallback(
    (openValues: string[]) => {
      // Handle account groups
      groups.forEach((group) => {
        const accordionValue = createAccordionValue(group.name);
        const isOpen = openValues.includes(accordionValue);
        setAccountNavGroupCollapsed(budgetId, group.name, !isOpen);
      });

      // Handle archived section
      const isArchivedOpen = openValues.includes('archived');
      setAccountNavGroupCollapsed(budgetId, 'Archived', !isArchivedOpen);
    },
    [groups, budgetId, setAccountNavGroupCollapsed],
  );

  // Show welcome box if no accounts exist
  if (hasNoAccounts) {
    return (
      <div className="px-2 lg:px-4 mt-4">
        <Card className="border-dashed">
          <CardHeader className="pb-4">
            <CardTitle className="text-base font-semibold">
              Welcome to <span className="text-sky-600">Liquid</span>Budget!
            </CardTitle>
            <div className="text-sm font-normal">
              Let's start by adding your first account. I recommend beginning with your cash on hand.
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground mt-1 mb-2">
              <div className="flex items-center gap-2">
                <HandCoins className="h-4 w-4" />
                <span>Cash</span>
              </div>
              <div className="flex items-center gap-2">
                <Wallet className="h-4 w-4" />
                <span>Checking</span>
              </div>
              <div className="flex items-center gap-2">
                <PiggyBank className="h-4 w-4" />
                <span>Savings</span>
              </div>
              <div className="flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                <span>Credit Card</span>
              </div>
            </div>

            <AccountAddDialog>
              <Button size="sm" className="w-full" variant="default">
                <HandCoins className="mr-2 h-4 w-4" />
                <span>Add Your First Account</span>
              </Button>
            </AccountAddDialog>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show normal account navigation if accounts exist
  return (
    <Accordion type="multiple" className="w-full" value={accordionValues} onValueChange={handleAccordionChange}>
      <AccountNavGroups />
      <AccountNavArchived />
    </Accordion>
  );
}
